/**
 * The drawing placement tool (planning plan P4-4b): its Settings switch, off by default, and the panel that moves a
 * local-coordinate DXF to its true coordinates in the browser. Wording is neutral about who the authority is, because
 * the switch reaches every tenant. No layer name appears here: layer names come from the profile and the file. ms and
 * zh use the same placeholders (planning-placement-keys.spec.ts).
 */
export default {
  settings: {
    title: 'Drawing placement tool',
    enabled: 'Offer the drawing placement tool',
    hint: 'Off by default. When on, the drawing standard panel offers a tool that moves a DXF to its true coordinates from two typed points, in the browser. The check itself does not change.',
    warning:
      "Whether this tool is acceptable, or whether coordinates must come only from a licensed surveyor's drawing, has not been confirmed. A placed file carries no mark, so turning this off later cannot identify drawings already placed.",
    noBoundary:
      'No jurisdiction boundary is set, so applicants will not be offered this tool: without one, nothing checks where a placed drawing lands.',
  },
  open: 'Place my drawing at its true coordinates',
  hint: 'For a drawing made at local coordinates. Your file stays in this browser until you submit it.',
  note: 'Type coordinates from the certified survey plan. This tool does not certify them, and the full check still runs on what you submit.',
  close: 'Close the placement tool',
  fileLabel: 'DXF file',
  reading: 'Reading the drawing…',
  unitLabel: 'The unit the drawing is in',
  unitChoose: 'Choose a unit',
  units: { m: 'Metres', mm: 'Millimetres', cm: 'Centimetres', ft: 'Feet' },
  crsFirst: 'Choose the coordinate system first.',
  points: { a: 'Point A', b: 'Point B', check: 'Check point' },
  vertexLabel: 'Point in the drawing',
  vertexChoose: 'Choose a point',
  listCut: 'Only the first {{count}} points are listed.',
  verticesCut: 'This drawing has more points than can be listed. Each layer lists only its first {{limit}}.',
  east: 'Easting, {{crs}}',
  north: 'Northing, {{crs}}',
  checkHint:
    'A third point, not used to place the drawing. It is required because two points alone cannot tell whether a drawing is mirrored or whether points were swapped.',
  drawingTitle: 'Your drawing',
  placedTitle: 'The placed drawing, north up',
  compute: 'Work out the placement',
  result:
    'Scale: 1 drawing unit = {{factor}} m. Rotation: {{rotation}} degrees. Point A is {{a}} mm from its coordinates, point B {{b}} mm.',
  resultCheck: 'The check point is {{off}} mm from its coordinates.',
  resultCrs: 'The coordinates are in {{crs}}. Declare the same coordinate system when you submit this drawing.',
  download: 'Download the placed DXF',
  downloadHint:
    'Open it in your CAD software and use Zoom Extents, because its saved view still points at the old place. Check it against the certified plan, then choose it as your drawing. Layers, block definitions and text are unchanged.',
  file: {
    dwg_file: 'This is a DWG file. Save it as DXF in your CAD software, then load it here.',
    binary_dxf: 'This is a binary DXF. Save it as ASCII DXF in your CAD software, then load it here.',
    not_dxf: 'This file is not a DXF.',
    truncated: 'This DXF has no end marker, so it may be incomplete. Save it again and reload it.',
    malformed: 'This DXF cannot be read at line {{line}}.',
    no_entities: 'This DXF has no drawing entities.',
    too_large: 'This file is larger than {{limit}} MB, the largest upload allowed here.',
    output_too_large:
      'The placed file would be {{size}} MB, over the {{limit}} MB upload limit. Remove what the drawing does not need and try again.',
  },
  refusedTitle: 'This tool cannot move the drawing because of what it holds:',
  refusedRow: '{{type}} on layer {{layer}}: {{count}} ({{reason}})',
  refusedFix: 'Delete or explode these in your CAD software, save as DXF and load it again.',
  reason: {
    not_allowed: 'a type this tool does not move',
    extrusion: 'drawn mirrored or in another plane',
    embedded_object: 'holds an embedded object',
    xdata_coordinates: 'carries coordinates in its extended data',
    polyline_mesh: 'a mesh',
    polyline_curve_fit: 'a curve-fitted polyline',
    spline_fit_points: 'a spline drawn by fit points',
    hatch_spline_edge: 'a hatch with a spline edge',
    hatch_gradient: 'a gradient hatch',
    xref_insert: 'an external reference',
    undefined_block: 'names a block the file does not define',
    mtext_direction: 'multi-line text rotated with no direction',
    drawing_object: 'a clip or a stored location that would go stale',
  },
  fit: {
    scale_mismatch:
      'In the chosen unit the two drawing points are {{drawing}} m apart, but the typed coordinates are {{typed}} m apart: more than 0.1 % different. Check the unit and the points.',
    degenerate_drawing: 'Points A and B are the same point. Choose two different points.',
    degenerate_typed: 'The coordinates typed for A and B are the same.',
    bad_number: 'Type each coordinate as a plain number in metres, such as 412345.678, with no thousands separators.',
    out_of_range: 'A typed coordinate is too large for any coordinate system offered here. Check it.',
    baseline_too_short:
      'Points A and B are too close together: at least {{min}} m apart is needed, or a small typing error swings the far corner by metres.',
    check_point_missing: 'A check point is required: choose it in the drawing and type both of its coordinates.',
    mirrored:
      'The check point does not fit: the drawing looks mirrored against the typed coordinates, or a point or coordinate is swapped. Check them. If the drawing is mirrored, mirror it back in your CAD software.',
    check_point_off: 'The check point is {{off}} m from its typed coordinates. Check the points and the coordinates.',
    check_point_collinear:
      'The check point is almost on the line through A and B, so it proves nothing. Choose one further from that line.',
  },
  preview: {
    open: 'Preview on a map',
    warning:
      'Clicks are approximate and are never used for the downloaded file, which takes only the typed coordinates.',
    needPoints: 'Choose points A and B in the drawing first.',
    clickA: 'Click where point A is.',
    clickB: 'Click where point B is.',
    placed: 'Roughly where the drawing will land.',
    clear: 'Clear the clicks',
    map: 'Map preview of the placement',
  },
}
